import dialler from '@/api/dialler';
import router from '@/router';
import { findIndex, filter } from 'lodash';
import * as types from '../mutation-types';
import moment from 'moment';

const state = {
  activeCall: null,
  calls: [],
  outcomes: [],
  queues: [],
  selectedPhoneCall: null,
  selectedQueue: null,
  state: null
};

const getters = {
  ActiveCall: state => state.activeCall,
  DiallerCallbackOutcomes: state => filter(state.outcomes, { callback: true, manual_callback: false }),
  DiallerManualCallbackOutcomes: state => filter(state.outcomes, { callback: true, manual_callback: true }),
  DiallerNoCallbackOutcomes: state => filter(state.outcomes, { callback: false }),
  DiallerQueueCallList: state => state.calls,
  DiallerQueues: state => state.queues,
  DiallerState: state => state.state,
  GetDiallerQueue: state => index => state.queues[index],
  SelectedDiallerQueue: state => state.selectedQueue,
  SelectedPhoneCall: state => state.calls[state.selectedPhoneCall],
  TodaysCallCount: state => (state.queues.length > 0 ? state.queues[0].count : 0)
};

const actions = {
  getDiallerQueues({ commit }) {
    dialler.getQueues().then(queues => {
      commit(types.SET_DIALLER_QUEUES, queues);
    });
  },
  getDiallerQueueForUser({ commit, dispatch }, { diallerQueueId, userId, phoneCallId }) {
    dialler.getQueueForUser(diallerQueueId, userId).then(calls => {
      const queue = calls.data;
      if (queue.length > 0) {
        queue.sort((a, b) => (a.scheduled_time > b.scheduled_time ? 1 : -1));
      }
      if (phoneCallId === undefined && calls.data.length > 0) {
        router.replace({ name: 'Dialler', params: { diallerQueueId, phoneCallId: calls.data[0].id } });
      }
      commit(types.SET_DIALLER_CALLS, queue);
      dispatch('selectPhoneCall', phoneCallId);
    });
  },
  addCallToQueue({ commit, dispatch, state }, phoneCall) {
    const idx = findIndex(state.calls, { id: phoneCall.id });
    if (idx >= 0) {
      return true;
    }
    if (state.selectedQueue === state.queues[0] && phoneCall.is_scheduled_for_today) {
      commit(types.ADD_CALL_TO_QUEUE, phoneCall);
    }
    dispatch('getDiallerQueues');
    if (phoneCall.is_scheduled) {
      dispatch('addNotification', {
        text: `A new phone call has been scheduled for you at ${phoneCall.friendly_scheduled_time}`,
        colour: 'info'
      });
    } else {
      dispatch('addNotification', { text: 'A new phone call has been assigned to you', colour: 'info' });
    }
  },
  removeCallFromQueue({ commit, dispatch, state }, phoneCallId) {
    if (state.activeCall === null || phoneCallId !== state.activeCall.id) {
      dispatch('getDiallerQueues');
      const phoneCallId = state.calls.length > 1 ? state.calls[1].id : null;
      commit(types.REMOVE_CALL_FROM_QUEUE, phoneCallId);
      router.replace({
        name: 'Dialler',
        params: { diallerQueueId: state.selectedQueue.id, phoneCallId }
      });
    }
  },
  selectDiallerQueue({ commit, dispatch, getters, state }, { diallerQueueId, phoneCallId }) {
    const userId = getters.UserId;
    const diallerQueueIdx = findIndex(state.queues, { id: diallerQueueId });
    commit(types.SELECT_DIALLER_QUEUE, state.queues[diallerQueueIdx]);
    dispatch('getDiallerQueueForUser', { diallerQueueId, userId, phoneCallId });
  },
  selectPhoneCall({ commit, dispatch, state }, id) {
    if (typeof id !== undefined && id !== null && state.calls.length > 0) {
      const idx = findIndex(state.calls, { id });
      if (idx >= 0) {
        commit(types.SELECT_PHONE_CALL, idx);
        const phoneCall = state.calls[idx];

        if (phoneCall.subject.type === 'Referral') {
          dispatch('clearSelectedCustomer');
          dispatch('clearComments');
          dispatch('getComments', phoneCall.subject.id);
          dispatch('selectReferral', phoneCall.subject.id);
        } else {
          dispatch('clearSelectedReferral');
          dispatch('clearComments');
          dispatch('selectCustomer', phoneCall.subject.id);
        }
      }
    }
  },
  setPhoneCallAsRegistered({ commit, dispatch, state }, { phoneCallId, businessId, businessName, contact }) {
    const idx = findIndex(state.calls, { id: phoneCallId });
    commit(types.SET_PHONE_CALL_AS_REGISTERED, { phoneCallIndex: idx, businessId, businessName, contact });
    dispatch('selectPhoneCall', phoneCallId);
  },
  getPhoneCallOutcomes({ commit }) {
    dialler.getPhoneCallOutcomes().then(outcomes => commit(types.SET_PHONE_CALL_OUTCOMES, outcomes.data));
  },
  setActiveCall({ commit, dispatch }, phoneCall) {
    commit(types.SET_ACTIVE_CALL, phoneCall);
    dispatch('updateActiveCall', {
      start_time: moment().unix()
    });
  },
  clearActiveCall({ commit }) {
    commit(types.CLEAR_ACTIVE_CALL);
  },
  async updateActiveCall({ commit, state }, updates) {
    if (state.activeCall !== null) {
      commit(types.UPDATE_ACTIVE_CALL, updates);
      await dialler.updatePhoneCallRecord(state.activeCall.id, updates);
    }
  },
  markPhoneCallOverdue({ commit }, phoneCall) {
    commit(types.MARK_PHONE_CALL_OVERDUE, phoneCall);
  }
};

const mutations = {
  [types.SET_DIALLER_QUEUES](state, queues) {
    state.queues = queues;
  },
  [types.SET_DIALLER_CALLS](state, calls) {
    state.calls = calls;
  },
  [types.SELECT_DIALLER_QUEUE](state, queueId) {
    state.selectedQueue = queueId;
  },
  [types.ADD_CALL_TO_QUEUE](state, phoneCall) {
    let calls = state.calls.slice();
    calls.push(phoneCall);
    calls.sort((a, b) => (a.scheduled_time > b.scheduled_time ? 1 : -1));
    state.calls = calls;
    state.queues[0].count++;
  },
  [types.REMOVE_CALL_FROM_QUEUE](state, id) {
    const idx = findIndex(state.calls, { id });
    if (idx >= 0) {
      state.calls.splice(idx, 1);
      const queueIdx = findIndex(state.queues, { id: state.selectedQueue.id });
      state.queues[queueIdx].count--;
    }
  },
  [types.SELECT_PHONE_CALL](state, idx) {
    state.selectedPhoneCall = idx;
  },
  [types.SET_PHONE_CALL_AS_REGISTERED](state, { phoneCallIndex, businessId, businessName, contact }) {
    let call = state.calls[phoneCallIndex];
    call.subject = {
      id: businessId,
      name: businessName,
      contact,
      type: 'Customer'
    };
    state.calls[phoneCallIndex] = call;
    state.selectedPhoneCall = phoneCallIndex;
  },
  [types.SET_PHONE_CALL_OUTCOMES](state, outcomes) {
    state.outcomes = outcomes;
  },
  [types.SET_ACTIVE_CALL](state, phoneCall) {
    state.activeCall = phoneCall;
  },
  [types.CLEAR_ACTIVE_CALL](state) {
    state.activeCall = null;
  },
  [types.UPDATE_ACTIVE_CALL](state, updates) {
    state.activeCall = Object.assign(state.activeCall, updates);
    const idx = findIndex(state.calls, { id: state.activeCall.id });
    state.calls[idx] = state.activeCall;
  },
  [types.MARK_PHONE_CALL_OVERDUE](state, phoneCall) {
    const idx = findIndex(state.calls, { id: phoneCall.id });
    state.calls[idx].overdue = true;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
